import { useState, useEffect } from 'react'

interface DataConsentModalProps {
  isOpen: boolean
  onAccept: () => void
  onCancel: () => void
  loading?: boolean
}

export function DataConsentModal({ isOpen, onAccept, onCancel, loading }: DataConsentModalProps) {
  const [agreed, setAgreed] = useState(false)

  // Reset checkbox when modal opens
  useEffect(() => {
    if (isOpen) setAgreed(false)
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="font-heading text-2xl text-primary mb-1">
            Privacy Notice & Informed Consent
          </h2>
          <p className="text-xs text-neutral-500 mb-4">
            Pursuant to the General Data Protection Regulation (EU) 2016/679 (GDPR)
          </p>

          <div className="space-y-4 text-neutral-700 text-sm leading-relaxed">
            <p>
              This document outlines the terms under which <strong>MetaGames LAB Ltd.</strong> (the "Data Controller", registered at Agias Fylaxeos, 95 1st floor, 3087, Limassol, Cyprus; email:{' '}
              <a href="mailto:info@metagames.academy" className="text-primary hover:underline">info@metagames.academy</a>
              ) processes your personal data during "The New King" corporate event.
            </p>

            {/* 1. Purpose */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-2">1. Purpose and Strict Limitation of Processing</h3>
              <p className="mb-2">Your personal data is collected and processed strictly and exclusively for the following purposes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Core Game Mechanics:</strong> Processing voice commands and interactions with AI agents via the Web Application.</li>
                <li><strong>General Analytics:</strong> Generating an Anonymized General Report for the event sponsor (your employer).</li>
                <li><strong>Personal Analytics (Optional):</strong> Generating a Personal Performance Report (behavioral and negotiation analytics) for your individual use, based on behavioral pattern.</li>
              </ul>
              <p className="mt-2 text-xs text-neutral-500">Data will not be used for marketing, sold to third parties, or used for any purposes outside the scope of this event.</p>
            </div>

            {/* 2. Data Categories */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-2">2. Data Categories Collected</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Identity Data:</strong> First name, Last name, Email address.</li>
                <li><strong>Application Data:</strong> Actions, votes, and inputs submitted through the Web Application.</li>
                <li><strong>AI Interaction Audio:</strong> Voice recordings during interactions with AI agents and public speeches during gameplay.</li>
              </ul>
            </div>

            {/* 3. Confidentiality */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-2">3. Strict Confidentiality and Data Sharing</h3>
              <p className="mb-2">We enforce strict access controls regarding the generated analytics:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Personal Report:</strong> Sent exclusively to you. Your employer, HR department, and other participants will not have access to your personal metrics, transcripts, or behavioral profile.</li>
                <li><strong>General Report:</strong> The event sponsor (your employer) receives only aggregated data at team level regarding the event's overall dynamics and patterns demonstrated by the team.</li>
              </ul>
              <p className="mt-2">
                <strong>Third-Party Processors:</strong> We utilize secure AI APIs (e.g., OpenAI, Deepgram, ElevenLabs) strictly as data processors. We operate under "Zero Data Retention" agreements, ensuring your data is not used to train their public AI models.
              </p>
            </div>

            {/* 4. Data Retention */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-2">4. Data Retention Periods</h3>
              <p className="mb-2">In compliance with the GDPR storage limitation principle, your data will be permanently deleted according to the following schedule:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Raw Audio Recordings:</strong> Permanently deleted within 7 days post-event.</li>
                <li><strong>Transcripts and Generated Reports:</strong> Permanently deleted within 30 days post-event.</li>
              </ul>
            </div>

            {/* 5. Data Subject Rights */}
            <div>
              <h3 className="font-semibold text-neutral-900 mb-2">5. Data Subject Rights</h3>
              <p>
                Under the GDPR, you have the right to access, rectify, or request early erasure of your personal data. To exercise these rights or withdraw consent, contact{' '}
                <a href="mailto:info@metagames.academy" className="text-primary hover:underline">info@metagames.academy</a>.
              </p>
            </div>

            {/* Declaration of Consent */}
            <div className="bg-neutral-100 p-4 rounded-lg border border-neutral-200">
              <h3 className="font-semibold text-neutral-900 mb-3 text-sm uppercase tracking-wide">Declaration of Consent</h3>
              <p className="mb-2">By giving this consent, I hereby acknowledge and agree to the following:</p>
              <ol className="list-decimal list-inside space-y-2 ml-2">
                <li>I have read and fully understood the terms of data processing, the data retention periods, and my rights under the GDPR.</li>
                <li>I expressly consent to the processing of my Identity Data, Application Data, and Voice Interactions with AI agents for the purpose of participating in the event and generating the Anonymized General Report.</li>
                <li>I understand that I may withdraw my consent at any time without any negative consequences to my employment or participation, though it will not affect the lawfulness of processing based on consent before its withdrawal.</li>
              </ol>
            </div>

            {/* Explicit consent checkbox */}
            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={loading}
                className="mt-0.5 w-4 h-4 accent-primary shrink-0"
              />
              <span className="text-sm text-neutral-800 font-medium">
                I have read and agree to the terms described above
              </span>
            </label>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-6 py-3 border-2 border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onAccept}
              disabled={!agreed || loading}
              className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Consent and Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
